import _ from 'lodash';
import { set } from 'vue-demi';
import { defineStore } from 'pinia';

import { ISession, ITrigger, Writeable } from '@/types';
import { SESSION_STATUS } from '@/constants';
import { CONTACT_HISTORY_STATUS } from './common';

export const useSessionsStore = defineStore('HITL-Sessions', {
  state: () => {
    return {
      sessionsById: {} as Record<string, ISession>,
      eventTimestampsById: {} as Record<string, Date>,
      conversationTriggers: [] as ITrigger[],
      /** Pagination state for the conversations panel */
      pagination: {
        nextId: '',
        current: 0,
        isLoading: false,
      },
      /** "Notify Agent" popup options */
      popup: {
        sessionId: '',
        settings: {},
        isContactLoading: false,
      },
      /** TODO: Do not load all history, instead request small chunk every time */
      historyStatus: {} as Record<string, CONTACT_HISTORY_STATUS>,
      applyingClaimActionById: {} as Record<string, boolean>,
      applyingStopActionById: {} as Record<string, boolean>,
      applyingTagsActionById: {} as Record<string, boolean>,
    };
  },
  getters: {
    sessions: state => Object.values(state.sessionsById),
    isNotifyPopupVisible: state => !!state.popup.sessionId,
    getHistoryStatus: state => (contactId: string) => {
      return state.historyStatus[contactId] || CONTACT_HISTORY_STATUS.NONE;
    },
    getSessionById: state => (sessionId: string) => {
      return state.sessionsById[sessionId];
    },
    isSessionActive: state => (sessionId: string) => {
      const ACTIVE_SESSION_STATUSES = [
        SESSION_STATUS.CLAIMED,
        SESSION_STATUS.ONGOING,
      ];

      const session = state.sessionsById[sessionId] || {};
      return ACTIVE_SESSION_STATUSES.includes(session.status);
    },
    isLoadingChunk: state => state.pagination.isLoading,
    isLoadingInitialChunk: state => {
      return state.pagination.isLoading && state.pagination.current === 0;
    },
    isLoadingNextChunk: state => {
      return state.pagination.isLoading && state.pagination.current > 0;
    },
    /** Indicates whether there are more sessions for active filter */
    isNextChunkAvailable: state => !!state.pagination.nextId,
    /**
     * In some cases search might try to sequentially request all sessions.
     * Prevent this by setting a hard limit of sessions,
     * that can be requested by conversations list.
     */
    isAtPaginationLimit: state => {
      const MAX_PAGINATION_LIMIT = 1000;
      return state.pagination.current >= MAX_PAGINATION_LIMIT;
    },
    isApplyingTagsAction: state => (sessionId: string) => {
      return state.applyingTagsActionById[sessionId] ?? false;
    },
    isApplyingClaimAction: state => (sessionId: string) => {
      return state.applyingClaimActionById[sessionId] ?? false;
    },
    isApplyingStopAction: state => (sessionId: string) => {
      return state.applyingStopActionById[sessionId] ?? false;
    },
  },
});

export const useSessionsSetters = () => {
  const store = useSessionsStore();

  /** Upsert sessions and update their timestamps if possible */
  const upsert = (args: ISession | ISession[]) => {
    const sessions = args instanceof Array ? args : [args];
    /* Merge updates into existing sessions when possible */
    const update = sessions.map(session => {
      return { ...store.sessionsById[session.sessionId], ...session };
    });

    const sessionsById = _.keyBy(update, 'sessionId');
    store.sessionsById = { ...store.sessionsById, ...sessionsById };

    for (const session of sessions) {
      updateEventTimestamp({
        sessionId: session.sessionId,
        timestamp: session.eventTimestamp,
      });
    }
  };

  /** When contact is merged or deleted we can remove sessions as well */
  const cleanSessionsByContactId = (contactId: string) => {
    const sessions = store.sessions.filter(item => item.contactId !== contactId);
    upsert(sessions);
  };

  /** Event timestamps can only be increased */
  const updateEventTimestamp = (args: {
    sessionId: string;
    timestamp?: number | string | Date;
  }) => {
    const { sessionId, timestamp } = args;
    const incoming = new Date(timestamp || 0);
    const current = store.eventTimestampsById[sessionId]
      ? store.eventTimestampsById[sessionId].getTime()
      : 0;

    if (incoming.getTime() >= current) {
      set(store.eventTimestampsById, sessionId, incoming);
    }
  };

  const toggleNotifyPopup = (options?: {
    sessionId: string;
    settings: object;
    isContactLoading: boolean;
  }) => {
    store.popup = options || { sessionId: '', settings: {}, isContactLoading: false };
  };

  const changeSessionStatus = (args: {
    sessionId: string;
    action: 'close' | 'transfer';
  }) => {
    const session: Writeable<ISession> = store.sessionsById[args.sessionId];
    if (!session) {
      return;
    }

    if (args.action === 'transfer') {
      session.status = SESSION_STATUS.ONGOING;
      return;
    }

    if (args.action === 'close') {
      if (store.popup.sessionId === args.sessionId) {
        toggleNotifyPopup();
      }

      session.needsAction = false;
      session.needsActionFrom = [];
      session.status = SESSION_STATUS.CLOSED;
    }
  };

  const setApplyingClaimAction = (sessionId: string, value: boolean) => {
    set(store.applyingClaimActionById, sessionId, value);
  };

  const setApplyingStopAction = (sessionId: string, value: boolean) => {
    set(store.applyingStopActionById, sessionId, value);
  };

  const setApplyingTagsAction = (sessionId: string, value: boolean) => {
    set(store.applyingTagsActionById, sessionId, value);
  };


  return {
    upsert,
    updateEventTimestamp,
    toggleNotifyPopup,
    cleanSessionsByContactId,
    changeSessionStatus,
    setApplyingClaimAction,
    setApplyingStopAction,
    setApplyingTagsAction,
  };
};
